import { groupBy } from 'lodash-es';
import {
  MultiSuggestLegItem,
  MultiSuggestRouteItem,
  MultiSuggestSegmentEnum,
  MultiSuggestSegmentItem,
} from '@lp/features/MultiSuggest';
import {
  AircraftLogisticsResourceType,
  AviaLegTypes,
  GroundLegTypes,
  RouteLegTypeEnum,
  SegmentTypeEnum,
  SupplementalLiftAviaLeg,
  TxCenterProviderAviaLeg,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { AIR_BLOCK_TIME } from '@/utils/route';

const getSegmentType = (
  currentLegSegmentType: SegmentTypeEnum,
  nextLegSegmentType?: SegmentTypeEnum,
  isOnlyDevice?: boolean,
): MultiSuggestSegmentEnum => {
  switch (currentLegSegmentType) {
    case SegmentTypeEnum.hospital:
      return MultiSuggestSegmentEnum.TeamToDH;
    case SegmentTypeEnum.txcenter:
      return MultiSuggestSegmentEnum.DHToTX;
    case SegmentTypeEnum.hub:
    case SegmentTypeEnum.custom:
      return isOnlyDevice ? MultiSuggestSegmentEnum.DeviceToHub : MultiSuggestSegmentEnum.TeamToHub;
    case SegmentTypeEnum.reposition:
      return nextLegSegmentType === SegmentTypeEnum.hospital
        ? MultiSuggestSegmentEnum.EmptyToDH
        : MultiSuggestSegmentEnum.EmptyToTX;
    case SegmentTypeEnum.returnToBase:
    default:
      return MultiSuggestSegmentEnum.EmptyToAviaHub;
  }
};

type GetMultiSuggestRouteType = {
  segments: MultiSuggestSegmentItem[];
  routeDurationInMs?: number;
  blockTimeRouteDurationInMs: number;
};

const getMultiSuggestRoute = (legs: (AviaLegTypes | GroundLegTypes)[], isAvia: boolean): GetMultiSuggestRouteType => {
  const currentLegs = legs.reduce<MultiSuggestLegItem[]>((acc, leg, index, array) => {
    const isOnlyDevice = leg.resources.every((r) => r.resourceType === CaseResourceType.OCSConsole);
    const segmentType = getSegmentType(leg.segmentType, array[index + 1]?.segmentType, isOnlyDevice);

    const multiSuggestLeg: MultiSuggestLegItem = {
      id: leg.id,
      segmentType,
      departureLocationId: leg.departureLocation.locationId,
      arrivalLocationId: leg.arrivalLocation.locationId,
      departureTime: leg.departureTime,
      arrivalTime: leg.arrivalTime,
      departureLocalTimezone: leg.departureLocalTimezone,
      arrivalLocalTimezone: leg.arrivalLocalTimezone,
      timeDurationInMs: new Date(leg.arrivalTime).getTime() - new Date(leg.departureTime).getTime(),
    };

    return [...acc, multiSuggestLeg];
  }, []);

  const blockTimeRouteDurationInMs = currentLegs.reduce((acc, leg) => acc + leg.timeDurationInMs, 0);

  const groupBySegment = groupBy(currentLegs, 'segmentType');

  return {
    segments: Object.keys(groupBySegment).map<MultiSuggestSegmentItem>((segmentType) => ({
      segmentType: segmentType as MultiSuggestSegmentEnum,
      legs: groupBySegment[segmentType],
    })),
    routeDurationInMs: isAvia ? blockTimeRouteDurationInMs - legs.length * AIR_BLOCK_TIME : undefined,
    blockTimeRouteDurationInMs,
  };
};

const getMultiSuggestDriveRoute = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  isAvia: boolean,
): GetMultiSuggestRouteType => {
  const groupLegsBySegment = groupBy(legs, 'segmentType');
  const segmentsList = Object.keys(groupLegsBySegment).map((segmentType) => ({
    segmentType: segmentType as SegmentTypeEnum,
    legs: groupLegsBySegment[segmentType],
  }));

  const currentLegs = segmentsList.reduce<MultiSuggestLegItem[]>((acc, segment) => {
    const isMainSegmentType = [SegmentTypeEnum.hub, SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter].includes(
      segment.segmentType,
    );

    if (!isMainSegmentType) return acc;

    const segmentType = getSegmentType(segment.segmentType);
    const startLeg = segment.legs[0];
    const endLeg = segment.legs[segment.legs.length - 1];

    const multiSuggestLeg: MultiSuggestLegItem = {
      id: startLeg.id,
      segmentType,
      departureLocationId: startLeg.departureLocation.locationId,
      arrivalLocationId: endLeg.arrivalLocation.locationId,
      departureTime: startLeg.departureTime,
      arrivalTime: endLeg.arrivalTime,
      departureLocalTimezone: startLeg.departureLocalTimezone,
      arrivalLocalTimezone: endLeg.arrivalLocalTimezone,
      timeDurationInMs: new Date(endLeg.arrivalTime).getTime() - new Date(startLeg.departureTime).getTime(),
    };

    return [...acc, multiSuggestLeg];
  }, []);

  const blockTimeRouteDurationInMs = currentLegs.reduce((acc, leg) => acc + leg.timeDurationInMs, 0);

  const groupBySegment = groupBy(currentLegs, 'segmentType');

  return {
    segments: Object.keys(groupBySegment).map<MultiSuggestSegmentItem>((segmentType) => ({
      segmentType: segmentType as MultiSuggestSegmentEnum,
      legs: groupBySegment[segmentType],
    })),
    routeDurationInMs: isAvia ? blockTimeRouteDurationInMs - legs.length * AIR_BLOCK_TIME : undefined,
    blockTimeRouteDurationInMs,
  };
};

const getTmdxJetRoute = (
  aircraft: AircraftLogisticsResourceType,
  legs: (AviaLegTypes | GroundLegTypes)[],
): GetMultiSuggestRouteType => {
  const aviaLegs = legs.filter(
    (leg): leg is AviaLegTypes =>
      leg.legType === RouteLegTypeEnum.TmdxJetAviaLeg &&
      leg.resources.some((r) => r.resourceId === aircraft.resourceId),
  );

  return getMultiSuggestRoute(aviaLegs, true);
};

const getSLPRoutes = (legs: (AviaLegTypes | GroundLegTypes)[]): MultiSuggestRouteItem[] => {
  const slpLegs = legs.filter(
    (leg): leg is SupplementalLiftAviaLeg => leg.legType === RouteLegTypeEnum.SupplementalLiftAviaLeg,
  );

  return slpLegs.map((leg) => {
    const { segments, routeDurationInMs, blockTimeRouteDurationInMs } = getMultiSuggestRoute([leg], true);

    return {
      providerType: RouteLegTypeEnum.SupplementalLiftAviaLeg,
      title: leg.tailNumber || '',
      routeDurationInMs,
      blockTimeRouteDurationInMs,
      segments,
    };
  });
};

const getTXAviaRoutes = (legs: (AviaLegTypes | GroundLegTypes)[]): MultiSuggestRouteItem[] => {
  const slpLegs = legs.filter(
    (leg): leg is TxCenterProviderAviaLeg => leg.legType === RouteLegTypeEnum.TxCenterProviderAviaLeg,
  );

  return slpLegs.map((leg) => {
    const { segments, routeDurationInMs, blockTimeRouteDurationInMs } = getMultiSuggestRoute([leg], true);

    return {
      providerType: RouteLegTypeEnum.TxCenterProviderAviaLeg,
      title: leg.tailNumber || '',
      routeDurationInMs,
      blockTimeRouteDurationInMs,
      segments,
    };
  });
};

const getDriveRoutes = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  legTypes: RouteLegTypeEnum[],
  providerType: RouteLegTypeEnum,
): MultiSuggestRouteItem => {
  const driveLegs = legs.filter((leg): leg is GroundLegTypes => legTypes.includes(leg.legType));

  const { segments, routeDurationInMs, blockTimeRouteDurationInMs } = getMultiSuggestDriveRoute(driveLegs, false);

  return {
    providerType,
    title: '',
    segments,
    routeDurationInMs,
    blockTimeRouteDurationInMs,
  };
};

type GetRoutesByTransportParams = {
  legs: (AviaLegTypes | GroundLegTypes)[];
  aircrafts: AircraftLogisticsResourceType[];
  isSuggested?: boolean;
};

export const getRoutesByTransport = ({
  legs,
  aircrafts,
  isSuggested = false,
}: GetRoutesByTransportParams): MultiSuggestRouteItem[] => {
  const aircraftsRoutes = aircrafts
    .map((aircraft) => {
      const { segments, routeDurationInMs, blockTimeRouteDurationInMs } = getTmdxJetRoute(aircraft, legs);

      return {
        providerType: RouteLegTypeEnum.TmdxJetAviaLeg,
        title: aircraft.tailNumber,
        routeDurationInMs,
        blockTimeRouteDurationInMs,
        segments,
      };
    })
    .filter((r) => r.segments.length > 0);

  const slpRoutes = getSLPRoutes(legs);
  const txAviaRoutes = getTXAviaRoutes(legs);

  const aviaRoutes = [...aircraftsRoutes, ...slpRoutes, ...txAviaRoutes];

  if (!aviaRoutes.length && isSuggested) {
    const affiliateLegs = getDriveRoutes(
      legs,
      [RouteLegTypeEnum.TmdxAffiliateGroundLeg, RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg],
      RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    );

    return [affiliateLegs];
  }

  return aviaRoutes;
};
